import React, { useMemo } from 'react';
import { useTheme } from '@/context/ThemeContext';
import { SEASONAL_PRESETS } from '@/lib/seasonalThemes';
import { Flag } from '@/components/season/flags';

/**
 * Immersive, portal-wide decorative overlay for the active seasonal theme.
 * Purely cosmetic: fixed + pointer-events-none, so it never blocks clicks.
 *
 * Two layers, both driven by the active theme:
 *   1. Ambient atmosphere (per `ambient`): gradient glows, light rays,
 *      bokeh, string lights, flag heraldry — the "premium art-directed"
 *      backdrop the whole portal sits on.
 *   2. Particles (per `accent_effect`): snow / leaves / petals / sparkles.
 */
const PARTICLE = {
  snow: { count: 34, color: '#ffffff', size: [4, 9], round: '9999px', opacity: 0.85 },
  leaves: { count: 22, color: 'hsl(var(--brand))', size: [8, 14], round: '40% 60% 55% 45%', spin: true, opacity: 0.55 },
  petals: { count: 22, color: 'hsl(var(--brand-light))', size: [7, 12], round: '50% 50% 50% 0', spin: true, opacity: 0.5 },
  sparkle: { count: 30, color: 'hsl(var(--brand-light))', size: [3, 7], round: '9999px', opacity: 0.95, twinkle: true },
  hearts: { count: 18, color: 'hsl(345 75% 58%)', size: [9, 15], heart: true, opacity: 0.5 },
};

function AmbientLayer({ ambient }) {
  switch (ambient) {
    case 'gold-night':
      return (
        <>
          <div className="absolute inset-0" style={{ background: 'radial-gradient(70% 45% at 50% -5%, hsl(45 92% 55% / 0.18), transparent 70%)' }} />
          <div className="absolute inset-0" style={{ background: 'radial-gradient(120% 90% at 50% 120%, hsl(222 60% 10% / 0.10), transparent 60%)' }} />
        </>
      );
    case 'rays':
      return (
        <>
          <div className="absolute inset-0" style={{ background: 'radial-gradient(60% 50% at 22% -10%, hsl(42 92% 70% / 0.26), transparent 65%)' }} />
          {[0, 1, 2, 3, 4].map((i) => (
            <div key={i} className="absolute kr-ray" style={{
              top: '-30%', left: `${6 + i * 11}%`, width: '8%', height: '150%',
              background: 'linear-gradient(to bottom, hsl(42 92% 72% / 0.22), transparent 70%)',
              transform: `rotate(${12 + i * 4}deg)`, transformOrigin: 'top center',
              animation: `kr-ray-sway ${7 + i}s ease-in-out ${i * 0.6}s infinite alternate`,
            }} />
          ))}
        </>
      );
    case 'mosaic':
      return (
        <>
          <div className="absolute inset-0" style={{ background: 'radial-gradient(45% 40% at 0% 0%, hsl(18 85% 55% / 0.16), transparent 60%)' }} />
          <div className="absolute inset-0" style={{ background: 'radial-gradient(45% 40% at 100% 100%, hsl(250 60% 45% / 0.14), transparent 60%)' }} />
          <div className="absolute inset-0" style={{ background: 'radial-gradient(40% 35% at 100% 0%, hsl(43 92% 60% / 0.12), transparent 60%)' }} />
        </>
      );
    case 'beams':
      return (
        <>
          <div className="absolute inset-0" style={{ background: 'radial-gradient(70% 45% at 50% -10%, hsl(var(--brand-light) / 0.22), transparent 65%)' }} />
          {[0, 1, 2].map((i) => (
            <div key={i} className="absolute kr-ray" style={{
              top: '-20%', left: `${28 + i * 16}%`, width: '10%', height: '130%',
              background: 'linear-gradient(to bottom, hsl(0 0% 100% / 0.16), transparent 70%)',
              transform: `rotate(${-6 + i * 6}deg)`, transformOrigin: 'top center',
              animation: `kr-ray-sway ${8 + i}s ease-in-out ${i * 0.7}s infinite alternate`,
            }} />
          ))}
        </>
      );
    case 'lights':
      return (
        <>
          <div className="absolute inset-0" style={{ background: 'radial-gradient(55% 45% at 85% 0%, hsl(43 90% 60% / 0.16), transparent 60%)' }} />
          <div className="absolute inset-0" style={{ background: 'radial-gradient(55% 45% at 10% 10%, hsl(150 55% 40% / 0.12), transparent 60%)' }} />
          <div className="absolute top-0 left-0 right-0 h-3" style={{ borderTop: '1px solid hsl(43 60% 60% / 0.35)' }}>
            {Array.from({ length: 22 }).map((_, i) => (
              <span key={i} style={{
                position: 'absolute', top: '2px', left: `${(i + 0.5) * (100 / 22)}%`,
                width: '6px', height: '6px', borderRadius: '9999px',
                background: ['hsl(43 95% 60%)', 'hsl(355 75% 55%)', 'hsl(150 55% 45%)'][i % 3],
                boxShadow: `0 0 6px 1px ${['hsl(43 95% 60% / 0.8)', 'hsl(355 75% 55% / 0.8)', 'hsl(150 55% 45% / 0.8)'][i % 3]}`,
                animation: `kr-twinkle ${1.8 + (i % 4) * 0.4}s ease-in-out ${i * 0.15}s infinite`,
              }} />
            ))}
          </div>
        </>
      );
    case 'sun':
      return <div className="absolute inset-0" style={{ background: 'radial-gradient(48% 48% at 92% -8%, hsl(38 95% 62% / 0.28), transparent 62%)' }} />;
    case 'frost':
      return (
        <>
          <div className="absolute inset-0" style={{ background: 'radial-gradient(65% 40% at 50% -10%, hsl(205 85% 70% / 0.20), transparent 65%)' }} />
          <div className="absolute inset-0" style={{ background: 'radial-gradient(100% 70% at 50% 120%, hsl(210 50% 88% / 0.16), transparent 60%)' }} />
        </>
      );
    case 'bloom':
      return (
        <>
          <div className="absolute inset-0" style={{ background: 'radial-gradient(42% 42% at 0% 100%, hsl(142 55% 50% / 0.18), transparent 60%)' }} />
          <div className="absolute inset-0" style={{ background: 'radial-gradient(42% 42% at 100% 100%, hsl(330 65% 70% / 0.16), transparent 60%)' }} />
          <div className="absolute inset-0" style={{ background: 'radial-gradient(70% 40% at 50% -10%, hsl(100 55% 65% / 0.10), transparent 60%)' }} />
        </>
      );
    case 'autumn':
      return (
        <>
          <div className="absolute inset-0" style={{ background: 'radial-gradient(50% 45% at 0% 0%, hsl(28 88% 55% / 0.22), transparent 60%)' }} />
          <div className="absolute inset-0" style={{ background: 'radial-gradient(52% 50% at 100% 100%, hsl(16 82% 48% / 0.18), transparent 60%)' }} />
          <div className="absolute inset-0" style={{ background: 'radial-gradient(120% 80% at 50% -12%, hsl(36 88% 60% / 0.14), transparent 55%)' }} />
        </>
      );
    case 'rose':
      return (
        <>
          <div className="absolute inset-0" style={{ background: 'radial-gradient(45% 42% at 0% 100%, hsl(345 78% 66% / 0.18), transparent 60%)' }} />
          <div className="absolute inset-0" style={{ background: 'radial-gradient(45% 42% at 100% 0%, hsl(350 72% 72% / 0.15), transparent 60%)' }} />
          <div className="absolute inset-0" style={{ background: 'radial-gradient(70% 45% at 50% 120%, hsl(43 80% 70% / 0.10), transparent 60%)' }} />
        </>
      );
    default:
      return null;
  }
}

function FlagStrip({ codes }) {
  if (!codes || codes.length === 0) return null;
  return (
    <div
      className="flex fixed bottom-4 sm:bottom-6 left-1/2 -translate-x-1/2 z-[6] items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-1.5 sm:py-2 rounded-full max-w-[94vw] overflow-x-auto"
      style={{
        background: 'hsl(0 0% 100% / 0.72)', backdropFilter: 'blur(10px)',
        border: '1px solid hsl(43 55% 55% / 0.4)', boxShadow: '0 6px 24px hsl(222 47% 20% / 0.18)',
      }}
      data-testid="seasonal-flag-strip"
    >
      {codes.map((code, i) => (
        <span key={code} style={{
          display: 'inline-block', width: '32px', height: '21px',
          borderRadius: '3px', overflow: 'hidden',
          boxShadow: '0 1px 4px hsl(222 47% 20% / 0.3)',
          animation: `kr-flag-wave ${3 + (i % 3) * 0.5}s ease-in-out ${i * 0.2}s infinite`,
        }}>
          <Flag code={code} style={{ width: '100%', height: '100%', display: 'block' }} />
        </span>
      ))}
    </div>
  );
}

export default function SeasonalAccents() {
  const { activeSeason } = useTheme();
  const template = activeSeason?.template;
  const preset = template ? SEASONAL_PRESETS[template] : null;
  const effect = activeSeason?.accent_effect && activeSeason.accent_effect !== 'none'
    ? activeSeason.accent_effect : null;
  const ambient = preset?.ambient && preset.ambient !== 'none' ? preset.ambient : null;
  const flags = preset?.flags || null;

  const particles = useMemo(() => {
    const cfg = PARTICLE[effect];
    if (!cfg) return [];
    return Array.from({ length: cfg.count }).map((_, i) => ({
      id: i,
      left: Math.random() * 100,
      size: cfg.size[0] + Math.random() * (cfg.size[1] - cfg.size[0]),
      delay: Math.random() * 12,
      duration: 9 + Math.random() * 9,
      drift: (Math.random() * 2 - 1) * 40,
      top: Math.random() * 100,
      tw: 2 + Math.random() * 2,
    }));
  }, [effect]);

  if (!activeSeason) return null;
  const cfg = effect ? PARTICLE[effect] : null;

  return (
    <div className="fixed inset-0 z-[5] overflow-hidden pointer-events-none" aria-hidden="true" data-testid="seasonal-accents">
      <AmbientLayer ambient={ambient} />
      <FlagStrip codes={flags} />
      {cfg && particles.map((p) => (
        <span
          key={p.id}
          style={{
            position: 'absolute',
            top: cfg.twinkle ? `${p.top}%` : '-24px',
            left: `${p.left}%`,
            width: `${p.size}px`,
            height: `${p.size}px`,
            background: cfg.color,
            borderRadius: cfg.heart ? 0 : cfg.round,
            clipPath: cfg.heart ? "path('M8 14 L1.5 7 A4 4 0 0 1 8 2 A4 4 0 0 1 14.5 7 Z')" : undefined,
            opacity: cfg.opacity,
            '--kr-drift': `${p.drift}px`,
            animation: cfg.twinkle
              ? `kr-twinkle ${p.tw}s ease-in-out ${p.delay}s infinite`
              : `kr-season-fall ${p.duration}s linear ${p.delay}s infinite`,
            willChange: 'transform, opacity',
          }}
        />
      ))}
    </div>
  );
}
